import React, { useState, useEffect } from 'react';
import './UserManager.css';
import '../styles/clean-dashboard.css';
import { getApiBaseUrl } from '../config/api';

const UserManager = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [instances, setInstances] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showAssignAccess, setShowAssignAccess] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAccess, setUserAccess] = useState({});
  const [originalUserAccess, setOriginalUserAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user'
  });
  const [editFormData, setEditFormData] = useState({
    username: '',
    role: 'user',
    password: '',
    changePassword: false
  });

  useEffect(() => {
    fetchUsers();
    fetchInstances();
  }, []);

  const fetchInstances = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/admin/instances`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setInstances(data.instances || []);
      }
    } catch (error) {
      console.error('Failed to fetch instances:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/users`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/create-user`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setFormData({ username: '', password: '', role: 'user' });
        setShowAddUser(false);
        fetchUsers();
      } else {
        alert('Failed to create user: ' + data.error);
      }
    } catch (error) {
      alert('Connection error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAccess = async (userId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/users/${userId}/access`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        const accessMap = {};
        data.access.forEach(instanceId => {
          accessMap[instanceId] = true;
        });
        setUserAccess(accessMap);
        setOriginalUserAccess({...accessMap});
      }
    } catch (error) {
      console.error('Failed to fetch user access:', error);
    }
  };

  // Update user access only in local state (not save to server yet)
  const updateUserAccess = (userId, instanceId, hasAccess) => {
    setUserAccess(prev => ({
      ...prev,
      [instanceId]: hasAccess
    }));
  };

  // Save all user access changes to server
  const saveUserAccess = async () => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const instanceIds = Object.keys(userAccess).filter(instanceId => userAccess[instanceId]);
      
      const response = await fetch(getApiBaseUrl(`/users/${selectedUser.id}/access`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          instanceIds: instanceIds.map(id => parseInt(id))
        }),
      });

      const data = await response.json();
      if (data.success) {
        alert('User access updated successfully!');
        setOriginalUserAccess({...userAccess});
        setShowAssignAccess(false);
      } else {
        alert('Failed to update access: ' + data.message);
      }
    } catch (error) {
      alert('Connection error: ' + error.message);
    }
  };

  const openEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditFormData({
      username: userToEdit.username,
      role: userToEdit.role,
      password: '',
      changePassword: false
    });
    setShowEditUser(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const token = localStorage.getItem('auth_token');
      
      // Prepare data to send
      const updateData = {
        username: editFormData.username,
        role: editFormData.role
      };
      
      // Include password only if user wants to change it
      if (editFormData.changePassword && editFormData.password) {
        updateData.password = editFormData.password;
      }
      
      const response = await fetch(getApiBaseUrl(`/users/${editingUser.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();
      if (data.success) {
        setShowEditUser(false);
        setEditingUser(null);
        fetchUsers();
        alert('User updated successfully!');
      } else {
        alert('Failed to update user: ' + data.message);
      }
    } catch (error) {
      alert('Connection error: ' + error.message);
    }
  };

  const openAssignAccess = (selectedUser) => {
    setSelectedUser(selectedUser);
    setShowAssignAccess(true);
    fetchUserAccess(selectedUser.id);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return '#dc3545';
      case 'admin': return '#ffc107';
      case 'user': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'user': return 'User';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="user-manager">
      <div className="user-header">
        <h2>User Management</h2>
        {user.role === 'super_admin' && (
          <button 
            className="add-user-btn"
            onClick={() => setShowAddUser(true)}
          >
            <span>+</span> Add User
          </button>
        )}
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>USERNAME</th>
              <th>ROLE</th>
              <th>CREATED</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>
                  <div className="user-info">
                    <div className="username">{u.username}</div>
                    {u.id === user.id && <span className="current-user-badge">YOU</span>}
                  </div>
                </td>
                <td>
                  <span className={`role-badge ${u.role}`}>
                    {getRoleLabel(u.role)}
                  </span>
                </td>
                <td>
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td>
                  <div className="status-indicator active">
                    Active
                  </div>
                </td>
                <td>
                  {(user.role === 'super_admin' || user.role === 'admin') && u.role !== 'super_admin' && (
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        onClick={() => openEditUser(u)}
                        title="Edit User"
                      >
                        EDIT
                      </button>
                      <button
                        className="action-btn manage"
                        onClick={() => openAssignAccess(u)}
                        title="Manage Access"
                      >
                        MANAGE ACCESS
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddUser && user.role === 'super_admin' && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New User</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAddUser(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    placeholder="Enter username"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Enter password"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                <div className="button-group">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddUser(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}      {/* Edit User Modal */}
      {showEditUser && editingUser && (
        <div className="edit-user-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit User: {editingUser.username}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowEditUser(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditUser} className="user-form">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={editFormData.username}
                  onChange={(e) => setEditFormData({...editFormData, username: e.target.value})}
                  placeholder="Enter username"
                  required
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                >
                  <option value="user">User</option>
                  {user.role === 'super_admin' && (
                    <>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </>
                  )}
                </select>
              </div>

              <div className="form-group password-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editFormData.changePassword}
                    onChange={(e) => setEditFormData({
                      ...editFormData, 
                      changePassword: e.target.checked,
                      password: e.target.checked ? editFormData.password : ''
                    })}
                  />
                  <span className="checkbox-text">Change Password</span>
                </label>
              </div>

              {editFormData.changePassword && (
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({...editFormData, password: e.target.value})}
                    placeholder="Enter new password"
                    required={editFormData.changePassword}
                    minLength="6"
                  />
                  <small className="password-hint">Password must be at least 6 characters long</small>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowEditUser(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Access Modal */}
      {showAssignAccess && selectedUser && (
        <div className="assign-access-modal">
          <div className="modal-content access-modal-content">
            <div className="modal-header">
              <h3>Manage Access for {selectedUser.username}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAssignAccess(false)}
              >
                ×
              </button>
            </div>

            <div className="access-list">
              <div className="access-header">
                <h4>WhatsApp Instances</h4>
                <p>Select which instances this user can access:</p>
              </div>

              <div className="instances-grid">
                {instances.map(instance => (
                  <div key={instance.id} className="access-item">
                    <div className="instance-info">
                      <div className="instance-name">
                        {instance.display_name || instance.phone_number}
                      </div>
                      <div className="instance-details">
                        {instance.phone_number} • <span className={`status ${instance.status}`}>{instance.status}</span>
                      </div>
                    </div>
                    <div className="access-control">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={userAccess[instance.id] || false}
                          onChange={(e) => updateUserAccess(selectedUser.id, instance.id, e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowAssignAccess(false)}
              >
                Cancel
              </button>
              <button
                className="save-btn"
                onClick={saveUserAccess}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
